import React, { useEffect, useMemo } from 'react';

// eslint-disable-next-line import/no-extraneous-dependencies -- '@cornerstonejs/core' should be listed in the project's dependencies. Run 'npm i -S @cornerstonejs/core' to add it
import { getRenderingEngine } from '@cornerstonejs/core';
import styled from '@emotion/styled';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@repo/videa-components/src/index';
import { Dialog, DialogContent } from '@repo/videa-components/src/ui/dialog';
import classNames from 'classnames';
import { useAtom, useAtomValue } from 'jotai';
import { useResizeDetector } from 'react-resize-detector';

import { SegmentationsHatchPatterns } from '../../../../common-components/elements/icon/SegmentationsHatchPatterns';
import { useGetImageWithSegmentations } from '../../../../react-query/useSegmentations';
import { annotationStyles } from '../../../../theme/annotationStyles';
import { renderingEngineAtom } from '../../../../utils/cornerstone/cornerstoneAtoms';
import { RENDERING_ENGINE } from '../../../../utils/cornerstone/initializeCornerstone';
import { useCornerstone } from '../../../../utils/cornerstone/useCornerstone';
import { useDicomCanvas } from '../../../../utils/cornerstone/useDicomCanvas';
import { computeCursorClass } from '../../annotation-utils/annotation-utils';
import { viewerModeAtom } from '../../atoms';
import { toothPartsTogglesAtom } from '../../findingSidebar/atoms';
import type { ImageObject } from '../../types';
import {
  isFullScreenOpenAtom,
  isHQEnabledAtom,
  showSegmentationAtom,
} from '../imageToolbar/atoms';

// Styled component to inject annotation styles - TODO: migrate util to tailwind
const StyledFullScreenWrapper = styled.div`
  ${annotationStyles}

  .magnifyMode {
    cursor: zoom-in !important;
  }
  .drawMode {
    cursor: crosshair !important;
  }
  .default {
    cursor: pointer !important;
    &.user-annotation {
      cursor: crosshair !important;
    }
  }
`;

interface FullScreenImageViewerProps {
  activeImage: ImageObject;
}

const CornerstoneContent: React.FC<{
  activeImage: ImageObject;
}> = ({ activeImage }) => {
  const [dicomCanvas, setDicomCanvasRef] = useDicomCanvas();
  const [, setIsFullScreenOpen] = useAtom(isFullScreenOpenAtom);
  const isRenderingEngineInitialized = useAtomValue(renderingEngineAtom);
  const isHQEnabled = useAtomValue(isHQEnabledAtom);
  const showSegmentation = useAtomValue(showSegmentationAtom);
  const showAnatomySegmentation = useAtomValue(
    toothPartsTogglesAtom,
  ).tooth_anatomy;
  const viewerMode = useAtomValue(viewerModeAtom);
  const isImageWithSegmentations =
    useGetImageWithSegmentations(activeImage?.imageId) &&
    activeImage.detectedImageType !== 'COLOR_IMAGE';

  const renderingEngine = useMemo(
    () =>
      isRenderingEngineInitialized
        ? getRenderingEngine(RENDERING_ENGINE)
        : null,
    [isRenderingEngineInitialized],
  );

  const cursorClass = computeCursorClass(viewerMode);

  const { isCornerstoneReady, imageLoadStatus, loadImageFileIntoCanvas } =
    useCornerstone({
      dicomCanvasRef: dicomCanvas,
      isThumbnail: false,
      activeImage,
      viewportId: 'fullscreen-image-viewer',
      source: 'single-image-viewer', // Use same source as main viewer
    });

  const handleResize = () => {
    if (dicomCanvas && renderingEngine) {
      renderingEngine.resize();
    }
  };

  const { ref: containerRef } = useResizeDetector({
    handleHeight: true,
    handleWidth: true,
    skipOnMount: true,
    refreshMode: undefined,
    onResize: handleResize,
  });

  useEffect(() => {
    if (isCornerstoneReady && activeImage?.imageId && dicomCanvas) {
      loadImageFileIntoCanvas(isHQEnabled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeImage?.imageId, dicomCanvas, isCornerstoneReady, isHQEnabled]);

  // Ensure segmentation state is properly applied when viewport is ready
  useEffect(() => {
    if (imageLoadStatus === 'success' && renderingEngine) {
      // Force a resize to ensure segmentation state is properly applied
      setTimeout(() => {
        if (renderingEngine) {
          renderingEngine.resize();
        }
      }, 100);
    }
  }, [imageLoadStatus, renderingEngine]);

  // Cleanup when component unmounts
  useEffect(() => {
    return () => {
      // Cleanup cornerstone viewport when component unmounts
      if (renderingEngine) {
        try {
          const viewport = renderingEngine.getViewport(
            'fullscreen-image-viewer',
          );
          if (viewport) {
            // Disable the viewport to clean up resources
            renderingEngine.disableElement('fullscreen-image-viewer');
          }
        } catch (error) {
          // Catch cornerstone errors during cleanup
        }
      }
    };
  }, [renderingEngine]);

  const cornerstoneClasses = classNames('cornerstoneElement', {
    [cursorClass]: true,
    'patient-view': isImageWithSegmentations && showSegmentation,
    'tooth-parts':
      isImageWithSegmentations && showSegmentation && showAnatomySegmentation,
  });

  return (
    <StyledFullScreenWrapper
      data-testid="full-screen-image-viewer"
      ref={containerRef}
      className="vai-relative vai-flex vai-justify-center vai-items-center vai-h-full vai-w-full vai-bg-black"
    >
      <SegmentationsHatchPatterns />

      {imageLoadStatus === 'loading' && (
        <div className="vai-absolute vai-top-1/2 vai-left-1/2 vai-transform vai--translate-x-1/2 vai--translate-y-1/2 vai-z-5">
          <div className="vai-animate-spin vai-w-8 vai-h-8 vai-border-4 vai-border-white vai-border-t-transparent vai-rounded-full" />
        </div>
      )}

      <Button
        dataTestId="close-full-screen-trigger"
        className="vai-absolute vai-top-4 vai-right-4 vai-z-[1000] vai-bg-black vai-bg-opacity-50 vai-text-white vai-rounded-full vai-w-12 vai-h-12 hover:vai-bg-opacity-70"
        size="icon"
        onClick={() => setIsFullScreenOpen(false)}
      >
        <FontAwesomeIcon className="vai-text-slate-200" icon={faTimes} />
      </Button>

      <div
        ref={setDicomCanvasRef}
        className={cornerstoneClasses}
        style={{ width: '100%', height: '100%' }}
      />
    </StyledFullScreenWrapper>
  );
};

export const FullScreenImageViewer: React.FC<FullScreenImageViewerProps> = ({
  activeImage,
}) => {
  const [isFullScreenOpen, setIsFullScreenOpen] = useAtom(isFullScreenOpenAtom);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setIsFullScreenOpen(false);
    }
  };

  return (
    <Dialog open={isFullScreenOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="vai-fixed vai-inset-0 vai-z-[9999] vai-bg-black vai-border-0 vai-p-0 vai-shadow-none vai-max-w-none vai-max-h-none vai-w-full vai-h-full vai-translate-x-0 vai-translate-y-0 vai-rounded-none !vai-left-0 !vai-top-0 !vai-transform-none"
        hideClose
      >
        {isFullScreenOpen && <CornerstoneContent activeImage={activeImage} />}
      </DialogContent>
    </Dialog>
  );
};
